
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as exposed from '../exposed'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * TradingCentralNewsArticleInfo struct
 */
class TradingCentralNewsArticleInfo {
  /**
   * Initialize struct
   * @param {!string=} provider
   * @param {!string=} id
   * @param {!Date=} publishDate
   * @param {!string=} title
   * @constructor
   */
  constructor (provider = '', id = '', publishDate = new Date(0), title = '') {
    this.Provider = provider
    this.Id = id
    this.PublishDate = publishDate
    this.Title = title
  }

  /**
   * Copy struct (shallow copy)
   * @this {!TradingCentralNewsArticleInfo}
   * @param {!TradingCentralNewsArticleInfo} other Other struct
   * @returns {!TradingCentralNewsArticleInfo} This struct
   */
  copy (other) {
    if (other.Provider != null) {
      this.Provider = other.Provider
    } else {
      this.Provider = null
    }
    if (other.Id != null) {
      this.Id = other.Id
    } else {
      this.Id = null
    }
    if (other.PublishDate != null) {
      if (other.PublishDate instanceof Date) {
        this.PublishDate = new Date(other.PublishDate.getTime())
      } else {
        this.PublishDate = new Date(Math.round(other.PublishDate / 1000000))
      }
    } else {
      this.PublishDate = null
    }
    if (other.Title != null) {
      this.Title = other.Title
    } else {
      this.Title = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!TradingCentralNewsArticleInfo}
   * @returns {!TradingCentralNewsArticleInfo} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new TradingCentralNewsArticleInfoModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new TradingCentralNewsArticleInfoModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!TradingCentralNewsArticleInfo}
   * @param {!TradingCentralNewsArticleInfo} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof TradingCentralNewsArticleInfo)) {
      throw new TypeError('Instance of TradingCentralNewsArticleInfo is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!TradingCentralNewsArticleInfo}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Provider: ((this.Provider != null) ? this.Provider : null),
      Id: ((this.Id != null) ? this.Id : null),
      PublishDate: ((this.PublishDate != null) ? (this.PublishDate.getTime() * 1000000) : null),
      Title: ((this.Title != null) ? this.Title : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return TradingCentralNewsArticleInfo.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!TradingCentralNewsArticleInfo} other Object value
   * @returns {!TradingCentralNewsArticleInfo} Created struct
   */
  static fromObject (other) {
    return new TradingCentralNewsArticleInfo().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!TradingCentralNewsArticleInfo}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return TradingCentralNewsArticleInfo.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!TradingCentralNewsArticleInfo}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 5211
  }
}

export { TradingCentralNewsArticleInfo };

/**
 * Fast Binary Encoding TradingCentralNewsArticleInfo field model
 */
class FieldModelTradingCentralNewsArticleInfo extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Provider = new fbe.FieldModelString(buffer, 4 + 4)
    this.Id = new fbe.FieldModelString(buffer, this.Provider.fbeOffset + this.Provider.fbeSize)
    this.PublishDate = new fbe.FieldModelTimestamp(buffer, this.Id.fbeOffset + this.Id.fbeSize)
    this.Title = new fbe.FieldModelString(buffer, this.PublishDate.fbeOffset + this.PublishDate.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelTradingCentralNewsArticleInfo}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelTradingCentralNewsArticleInfo}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Provider.fbeSize + this.Id.fbeSize + this.PublishDate.fbeSize + this.Title.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelTradingCentralNewsArticleInfo}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Provider.fbeExtra + this.Id.fbeExtra + this.PublishDate.fbeExtra + this.Title.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelTradingCentralNewsArticleInfo}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelTradingCentralNewsArticleInfo.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelTradingCentralNewsArticleInfo}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 5211
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelTradingCentralNewsArticleInfo}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelTradingCentralNewsArticleInfo}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Provider.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Provider.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Provider.fbeSize

    if ((fbeCurrentSize + this.Id.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Id.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Id.fbeSize

    if ((fbeCurrentSize + this.PublishDate.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.PublishDate.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PublishDate.fbeSize

    if ((fbeCurrentSize + this.Title.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Title.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Title.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelTradingCentralNewsArticleInfo}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelTradingCentralNewsArticleInfo}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelTradingCentralNewsArticleInfo}
   * @param {!TradingCentralNewsArticleInfo} fbeValue Default value, defaults is new TradingCentralNewsArticleInfo()
   * @returns {!TradingCentralNewsArticleInfo} TradingCentralNewsArticleInfo value
   */
  get (fbeValue = new TradingCentralNewsArticleInfo()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelTradingCentralNewsArticleInfo}
   * @param {!TradingCentralNewsArticleInfo} fbeValue TradingCentralNewsArticleInfo value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Provider.fbeSize) <= fbeStructSize) {
      fbeValue.Provider = this.Provider.get()
    } else {
      fbeValue.Provider = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Provider.fbeSize

    if ((fbeCurrentSize + this.Id.fbeSize) <= fbeStructSize) {
      fbeValue.Id = this.Id.get()
    } else {
      fbeValue.Id = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Id.fbeSize

    if ((fbeCurrentSize + this.PublishDate.fbeSize) <= fbeStructSize) {
      fbeValue.PublishDate = this.PublishDate.get()
    } else {
      fbeValue.PublishDate = new Date(0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PublishDate.fbeSize

    if ((fbeCurrentSize + this.Title.fbeSize) <= fbeStructSize) {
      fbeValue.Title = this.Title.get()
    } else {
      fbeValue.Title = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Title.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelTradingCentralNewsArticleInfo}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelTradingCentralNewsArticleInfo}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelTradingCentralNewsArticleInfo}
   * @param {!TradingCentralNewsArticleInfo} fbeValue TradingCentralNewsArticleInfo value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelTradingCentralNewsArticleInfo}
   * @param {TradingCentralNewsArticleInfo} fbeValue TradingCentralNewsArticleInfo value
   */
  setFields (fbeValue) {
    this.Provider.set(fbeValue.Provider)
    this.Id.set(fbeValue.Id)
    this.PublishDate.set(fbeValue.PublishDate)
    this.Title.set(fbeValue.Title)
  }
}

export { FieldModelTradingCentralNewsArticleInfo };

/**
 * Fast Binary Encoding TradingCentralNewsArticleInfo model
 */
class TradingCentralNewsArticleInfoModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelTradingCentralNewsArticleInfo(this.buffer, 4)
  }

  /**
   * Get the TradingCentralNewsArticleInfo model
   * @this {!TradingCentralNewsArticleInfoModel}
   * @returns {!FieldModelTradingCentralNewsArticleInfo} model TradingCentralNewsArticleInfo model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!TradingCentralNewsArticleInfoModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!TradingCentralNewsArticleInfoModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return TradingCentralNewsArticleInfoModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!TradingCentralNewsArticleInfoModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelTradingCentralNewsArticleInfo.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!TradingCentralNewsArticleInfoModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!TradingCentralNewsArticleInfoModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!TradingCentralNewsArticleInfoModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!TradingCentralNewsArticleInfoModel}
   * @param {!TradingCentralNewsArticleInfo} value TradingCentralNewsArticleInfo value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!TradingCentralNewsArticleInfoModel}
   * @param {!TradingCentralNewsArticleInfo} value TradingCentralNewsArticleInfo value, defaults is new TradingCentralNewsArticleInfo()
   * @return {!object} Deserialized TradingCentralNewsArticleInfo value and its size
   */
  deserialize (value = new TradingCentralNewsArticleInfo()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new TradingCentralNewsArticleInfo(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new TradingCentralNewsArticleInfo(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!TradingCentralNewsArticleInfoModel}
   * @param {!number} prev Previous TradingCentralNewsArticleInfo model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { TradingCentralNewsArticleInfoModel };
